import React, { useState } from "react";
import { Filter, ChevronDown, ChevronUp } from "lucide-react";

/**
 * FilterPanel Component
 *
 * A collapsible filter panel container with optional clear filters button.
 * Reusable across admin sections.
 *
 * @param {React.ReactNode} children - Filter form fields
 * @param {boolean} hasActiveFilters - Whether any filters are currently applied
 * @param {Function} onClearFilters - Callback to clear all filters
 * @param {string} title - Panel title (default: "Filters")
 * @param {boolean} defaultOpen - Whether panel starts open (default: false)
 * @param {React.Component} icon - Custom icon component (default: Filter)
 *
 * @example
 * <FilterPanel
 *   hasActiveFilters={nameFilter !== ""}
 *   onClearFilters={() => setNameFilter("")}
 * >
 *   <input value={nameFilter} onChange={e => setNameFilter(e.target.value)} />
 * </FilterPanel>
 */
const FilterPanel = ({
  children,
  hasActiveFilters = false,
  onClearFilters,
  title = "Filters",
  defaultOpen = false,
  icon: Icon = Filter,
}) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg">
      {/* Header - Entire header is clickable */}
      <div
        className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
        onClick={() => setIsOpen(!isOpen)}
      >
        <div className="flex items-center gap-2 text-gray-700 dark:text-gray-300">
          <Icon className="w-5 h-5" />
          <span className="font-medium">{title}</span>
          {isOpen ? (
            <ChevronUp className="w-4 h-4" />
          ) : (
            <ChevronDown className="w-4 h-4" />
          )}
        </div>

        {hasActiveFilters && (
          <button
            onClick={(e) => {
              e.stopPropagation(); // Prevent header click when clearing
              onClearFilters();
            }}
            className="text-sm text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 font-medium transition-colors"
          >
            Clear Filters
          </button>
        )}
      </div>

      {/* Content - overflow-visible when open to allow dropdowns */}
      <div
        className={`transition-all duration-300 ease-in-out ${
          isOpen
            ? "max-h-[2000px] opacity-100 overflow-visible"
            : "max-h-0 opacity-0 overflow-hidden"
        }`}
      >
        <div className="p-4">{children}</div>
      </div>
    </div>
  );
};

export default FilterPanel;
